// src/components/ui/PasswordModal.tsx
import React, { useState } from 'react';
import { X, Lock, AlertCircle } from 'lucide-react';

interface PasswordModalProps {
  isOpen: boolean;
  fileName: string;
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
}

export const PasswordModal: React.FC<PasswordModalProps> = ({
  isOpen,
  fileName,
  onClose,
  onSubmit
}) => {
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(password);
      // Success - modal will be closed by parent
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 'var(--space-4)',
      fontFamily: 'var(--font-primary)'
    }}>
      <div style={{
        backgroundColor: 'var(--color-background)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        width: '100%',
        maxWidth: '400px',
        boxShadow: 'var(--shadow-xl)',
        position: 'relative'
      }}>
        {/* Close Button */}
        <button
          onClick={handleClose}
          disabled={isSubmitting}
          style={{
            position: 'absolute',
            top: 'var(--space-4)',
            right: 'var(--space-4)',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 'var(--space-1)',
            borderRadius: 'var(--radius)',
            transition: 'var(--transition-fast)',
            opacity: isSubmitting ? 0.5 : 1
          }}
          onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = 'var(--color-surface)')}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <X size={20} color="var(--color-muted)" />
        </button>

        {/* Header */}
        <div style={{
          marginBottom: 'var(--space-6)',
          paddingRight: 'var(--space-8)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-2)'
          }}>
            <Lock size={20} color="var(--color-text)" />
            <h3 style={{
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--color-text)',
              margin: 0,
              letterSpacing: 'var(--tracking-tight)'
            }}>
              Password Protected PDF
            </h3>
          </div>
          <p style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text)',
            margin: 0,
            lineHeight: 'var(--leading-relaxed)',
            fontWeight: 'var(--font-normal)'
          }}>
            This PDF requires a password to access. Please enter the password for <strong style={{ fontWeight: 'var(--font-medium)' }}>{fileName}</strong>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)'
        }}>
          <div>
            <label style={{
              display: 'block',
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              color: 'var(--color-text)',
              marginBottom: 'var(--space-2)'
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
              autoFocus
              placeholder="Enter PDF password"
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text)',
                backgroundColor: isSubmitting ? 'var(--color-surface)' : 'var(--color-background)',
                outline: 'none',
                transition: 'var(--transition-fast)',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-primary)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: 'var(--space-3)',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 'var(--radius)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>
              <AlertCircle size={16} color="var(--color-error)" />
              <span style={{
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-error)',
                fontWeight: 'var(--font-medium)'
              }}>
                {error}
              </span>
            </div>
          )}

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: 'var(--space-3)',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                padding: 'var(--space-3) var(--space-4)',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-primary)',
                backgroundColor: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                transition: 'var(--transition-fast)',
                opacity: isSubmitting ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                  e.currentTarget.style.borderColor = 'var(--color-muted)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                }
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting || !password.trim()}
              style={{
                padding: 'var(--space-3) var(--space-6)',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-background)',
                backgroundColor: isSubmitting || !password.trim() ? 'var(--color-muted)' : 'var(--color-primary)',
                border: 'none',
                borderRadius: 'var(--radius)',
                cursor: isSubmitting || !password.trim() ? 'not-allowed' : 'pointer',
                transition: 'var(--transition-fast)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting && password.trim()) {
                  e.currentTarget.style.backgroundColor = '#1f2937';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting && password.trim()) {
                  e.currentTarget.style.backgroundColor = 'var(--color-primary)';
                }
              }}
            >
              {isSubmitting && (
                <div style={{
                  width: 'var(--space-4)',
                  height: 'var(--space-4)',
                  border: '2px solid transparent',
                  borderTop: '2px solid var(--color-background)',
                  borderRadius: 'var(--radius-full)',
                  animation: 'spin 1s linear infinite'
                }} />
              )}
              {isSubmitting ? 'Processing...' : 'Process PDF'}
            </button>
          </div>
        </form>

        {/* CSS Animation */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};